/**
 * MarkdownTextPanel.tsx — Renders a multi-paragraph Markdown text panel with
 * optional height truncation via `ExpandableContent`.
 *
 * JSON trigger: `uiCompType.method: "renderMarkdownTextPanel"`
 * Data fields:
 *  - `sectionData.description` — Text content (string or string[])
 *  - `sectionData.text`        — Alias for `description`
 *
 * **Key options:**
 *  - `options.minHeight`   — If set, wraps content in `<ExpandableContent maxHeight>`
 *  - `options.parasCount`  — Limits the number of paragraphs rendered
 *  - `options.markdown`    — Force Markdown mode (default: `true` for this component)
 *
 * **Text pipeline:** Each string passes through `transformTextToHtml` which
 * handles all CMS directives (STYLE=..., ELINE, SPACE=..., etc.).
 *
 * **Style slots:** `uiCompType.styles` (outer wrapper).
 */
import React from "react";
import { SectionData, ComponentRenderProps } from "./index";
import { transformTextToHtml } from "./shared";
import ExpandableContent from "./ExpandableContent";

interface MarkdownTextPanelProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

const MarkdownTextPanel = ({ sectionData, props }: MarkdownTextPanelProps): JSX.Element | null => {
    const trackId = sectionData.trackId || "markdownTextPanel_";
    const items = sectionData.items as string[] | string;
    const { parasCount, minHeight } = sectionData.uiCompType?.options || {};
    const styles = sectionData.uiCompType?.styles || {};

    const panelClassName = trackId ? trackId + "_sectionDescription" : "sectionDescription";
    const [showMore, setShowMore] = React.useState(false);

    if (!items) return null;

    if (typeof items === "object" && Array.isArray(items)) {
        if (minHeight && typeof minHeight === 'number') {
            return (
                <ExpandableContent maxHeight={minHeight} props={props}>
                    {items.map((descItem: string, index: number) => transformTextToHtml(descItem, panelClassName, trackId + index, styles, undefined, props))}
                </ExpandableContent>
            );
        } else if (parasCount && typeof parasCount === 'number') {
            const showMoreParaCount = isNaN(parasCount) ? 1 : Math.max(0, parasCount);
            const firstPart = items.slice(0, showMoreParaCount);
            const secondPart = items.slice(showMoreParaCount);

            if (items.length <= showMoreParaCount) {
                 return (
                    <div style={{ ...styles }}>
                        {items.map((descItem: string, index: number) => (
                            transformTextToHtml(descItem, panelClassName, trackId + index, styles, undefined, props)
                        ))}
                    </div>
                );
            }

            return (
                <>
                    <div style={{ ...styles }}>
                        {firstPart.map((descItem: string, index: number) => {
                            let transformedText = transformTextToHtml(descItem, panelClassName, trackId + index, styles, undefined, props);
                            if (!showMore && index + 1 === firstPart.length && secondPart.length > 0) {
                                transformedText = React.createElement(
                                    React.Fragment,
                                    { key: `frag-${trackId}-${index}`},
                                    (transformedText as React.ReactElement<{ children?: React.ReactNode }>)?.props?.children || transformedText,
                                    <a key={`showMore-${trackId}`} href="#" onClick={(e) => { e.preventDefault(); setShowMore(true); }}
                                        style={{ fontWeight: 'bold', textDecoration: 'none', marginLeft: '5px', color: 'black' }}>
                                        ...More
                                    </a>
                                );
                            }
                            return transformedText;
                        })}
                    </div>
                    {showMore && secondPart.length > 0 && (
                        <div style={{ ...styles }}>
                            {secondPart.map((descItem: string, index: number) => {
                                let transformedText = transformTextToHtml(descItem, panelClassName, trackId + showMoreParaCount + index, styles, undefined, props);
                                if (index + 1 === secondPart.length) {
                                    transformedText = React.createElement(
                                        React.Fragment,
                                        { key: `frag-less-${trackId}-${index}`},
                                        (transformedText as React.ReactElement<{ children?: React.ReactNode }>)?.props?.children || transformedText,
                                        <a key={`showLess-${trackId}`} href="#" onClick={(e) => { e.preventDefault(); setShowMore(false); }}
                                            style={{ fontWeight: 'bold', textDecoration: 'none', marginLeft: '5px', color: 'black' }}>
                                            ...Less
                                        </a>
                                    );
                                }
                                return transformedText;
                            })}
                        </div>
                    )}
                </>
            );
        } else {
            return (
                <>
                    {(items as string[]).map((descItem: string, index: number) => (
                        transformTextToHtml(descItem, panelClassName, trackId + index, styles, undefined, props)
                    ))}
                </>
            );
        }
    } else {
        return transformTextToHtml(items as string, panelClassName, trackId, styles, undefined, props);
    }
}

export default MarkdownTextPanel;
